import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SectionId } from "../SettingsPage";

export interface SettingsNavItem {
  id: SectionId;
  label: string;
  icon: LucideIcon;
}

interface SettingsNavProps {
  items: SettingsNavItem[];
  activeId: SectionId;
  onChange: (id: SectionId) => void;
}

/**
 * Settings section nav styled with compact rounded-xl items and subtle active indicator.
 */
export function SettingsNav({ items, activeId, onChange }: SettingsNavProps) {
  return (
    <nav
      aria-label="Settings sections"
      className={cn(
        "md:sticky md:top-6 md:w-52 md:shrink-0",
        "-mx-4 flex gap-1 overflow-x-auto px-4 pb-2 md:mx-0 md:flex-col md:overflow-visible md:px-0 md:pb-0"
      )}
    >
      <div className="rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 p-1.5 shadow-xs flex md:flex-col gap-1 w-full backdrop-blur-xs">
        {items.map((item) => {
          const isActive = item.id === activeId;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "relative flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                isActive
                  ? "bg-white dark:bg-slate-800 text-primary shadow-xs border border-slate-200/80 dark:border-slate-700 font-bold"
                  : "text-slate-500 dark:text-slate-400 hover:bg-slate-100/60 dark:hover:bg-slate-800/60 hover:text-slate-800 dark:hover:text-slate-200"
              )}
            >
              <Icon className={cn("h-3.5 w-3.5 shrink-0", isActive ? "text-primary" : "text-slate-400 dark:text-slate-500")} aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
